import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Dialog from '@components/2-molecules/Dialog';
import Button, { BUTTON_VARIANT_OUTLINE, BUTTON_VARIANT_TEXT } from '@components/1-atoms/Button';
import Checkbox from '@components/1-atoms/Checkbox';
import { formatFileSize } from '@components/Chat/helpers';
import useAuthDownload from '@hooks/useAuthDownload';
import {
  listSkillFiles,
  addSkillFile,
  deleteSkillFile,
  skillFileUrl,
} from '@api/agentSkills';
import type { AgentSkill, AgentSkillRequest } from '@api/agentSkills';
import { axiosErrorMessage } from '@api/errors';
import {
  FormBody,
  FieldGroup,
  FieldLabel,
  FieldHint,
  FieldInput,
  FieldTextArea,
  FormError,
  FileList,
  FileRow,
  FileMeta,
  SkillActions,
  HiddenFileInput,
  ToolGrid,
  ToolOption,
  ToolOptionLabel,
  ToolBadge,
  CheckboxRow,
  CheckboxLabel,
} from './SkillsPage.styled';

export const NAME_PATTERN = /^[a-z][a-z0-9_-]{1,63}$/;
export const MAX_DESCRIPTION_LENGTH = 200;
export const MAX_INSTRUCTIONS_LENGTH = 20000;
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const MAX_BUNDLE_BYTES = 15 * 1024 * 1024;
export const MAX_FILES_PER_SKILL = 10;

const SKILL_FILES_QUERY_KEY = (skillId: string) => ['agentSkillFiles', skillId];
const TOOLS_LABEL_ID = 'skill-tools-label';

const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error ?? new Error('File read failed'));
    reader.readAsDataURL(file);
  });

/**
 * ``cloud``/``local`` mark a tool that only exists on that runner type — it is
 * still selectable (an org may author skills for its own local runners) but is
 * dropped from the agent's catalog on a session of the other type.
 */
export type ToolRunner = 'any' | 'cloud' | 'local';

export interface SelectableTool {
  name: string;
  runner: ToolRunner;
}

interface SkillFormDialogProps {
  skill: AgentSkill | null;
  /** Tools the org may select, in display order. */
  availableTools: SelectableTool[];
  isSaving: boolean;
  serverError: string | null;
  onClose: () => void;
  onSubmit: (request: AgentSkillRequest) => void;
}

const SkillFormDialog: React.FC<SkillFormDialogProps> = ({
  skill,
  availableTools,
  isSaving,
  serverError,
  onClose,
  onSubmit,
}) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [name, setName] = useState(skill?.name ?? '');
  const [description, setDescription] = useState(skill?.description ?? '');
  const [instructions, setInstructions] = useState(skill?.instructions ?? '');
  // Seeded from the intersection so a tool the catalog dropped, or one whose
  // entitlement lapsed, silently falls away on save instead of failing it.
  const [tools, setTools] = useState<string[]>(
    () => (skill?.tools ?? []).filter(
      (tool) => availableTools.some((available) => available.name === tool),
    ),
  );
  const [shared, setShared] = useState(skill?.shared ?? false);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toggleTool = (tool: string) =>
    setTools((current) =>
      current.includes(tool) ? current.filter((t) => t !== tool) : [...current, tool],
    );

  const runnerBadgeLabel = (runner: ToolRunner): string | null => {
    switch (runner) {
      case 'local':
        return intl.formatMessage({
          id: 'skills.form.localOnly',
          defaultMessage: 'Local only',
        });
      case 'cloud':
        return intl.formatMessage({
          id: 'skills.form.cloudOnly',
          defaultMessage: 'Cloud only',
        });
      case 'any':
        return null;
      default: {
        const exhaustive: never = runner;
        return exhaustive;
      }
    }
  };

  const skillId = skill && !skill.builtIn ? skill.id : null;
  const filesQuery = useQuery({
    queryKey: SKILL_FILES_QUERY_KEY(skillId ?? ''),
    queryFn: () => listSkillFiles(skillId ?? ''),
    enabled: !!skillId,
  });
  const invalidateFiles = () =>
    queryClient.invalidateQueries({ queryKey: SKILL_FILES_QUERY_KEY(skillId ?? '') });

  const files = filesQuery.data?.files ?? [];
  const usedBytes = files.reduce((total, file) => total + file.sizeBytes, 0);
  const atFileLimit = files.length >= MAX_FILES_PER_SKILL;
  const fileLimitMessage = intl.formatMessage(
    {
      id: 'skills.files.limitReached',
      defaultMessage: 'Maximum of {max} files reached. Remove a file to add another.',
    },
    { max: MAX_FILES_PER_SKILL },
  );

  const uploadMutation = useMutation({
    mutationFn: async (file: File) =>
      addSkillFile(skillId ?? '', {
        filename: file.name,
        contentBase64: await readAsBase64(file),
      }),
    onSuccess: () => {
      setFileError(null);
      invalidateFiles();
    },
    onError: (error) =>
      setFileError(
        axiosErrorMessage(
          error,
          intl.formatMessage(
            {
              id: 'skills.files.uploadError',
              defaultMessage:
                'Upload failed. Filenames may use letters, digits, ., _ and -; files up to {max}.',
            },
            { max: formatFileSize(MAX_FILE_SIZE_BYTES) },
          ),
        ),
      ),
  });

  const removeFileMutation = useMutation({
    mutationFn: (filename: string) => deleteSkillFile(skillId ?? '', filename),
    onSuccess: invalidateFiles,
  });

  const { download, downloading } = useAuthDownload();
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const downloadFile = async (filename: string) => {
    if (!skillId || downloading) return;
    setDownloadingFile(filename);
    try {
      await download(skillFileUrl(skillId, filename), filename);
    } finally {
      setDownloadingFile(null);
    }
  };

  // isFetching, not just isPending: the count only catches up once the list
  // refetch that follows an upload has landed. The file cap is not in here —
  // it turns on the filename, which only a picked file carries.
  const addFileDisabled = uploadMutation.isPending || filesQuery.isFetching;

  const onFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || addFileDisabled) return;
    // Ahead of the caps: an empty file is refused whatever room is left, and
    // the cap message below would promise a replacement that fails too.
    if (file.size === 0) {
      setFileError(
        intl.formatMessage({ id: 'skills.files.empty', defaultMessage: 'File is empty.' }),
      );
      return;
    }
    // A same-named file overwrites in place, so it takes no new slot and frees
    // the bytes of the one it replaces. Both caps count it as the server does.
    const replaced = files.find((f) => f.filename === file.name);
    if (!replaced && atFileLimit) {
      // Not fileLimitMessage: the hint below already carries it, and repeating
      // it verbatim says nothing about the file just turned away.
      setFileError(
        intl.formatMessage(
          {
            id: 'skills.files.limitReachedPick',
            defaultMessage:
              '{filename} was not added. Replacing one of the files above still works.',
          },
          { filename: file.name },
        ),
      );
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError(
        intl.formatMessage(
          { id: 'skills.files.tooLarge', defaultMessage: 'File too large (max {max}).' },
          { max: formatFileSize(MAX_FILE_SIZE_BYTES) },
        ),
      );
      return;
    }
    if (usedBytes - (replaced?.sizeBytes ?? 0) + file.size > MAX_BUNDLE_BYTES) {
      setFileError(
        intl.formatMessage(
          {
            id: 'skills.files.bundleTooLarge',
            defaultMessage:
              'That file would take this skill over its {max} total. Remove a file to make room.',
          },
          { max: formatFileSize(MAX_BUNDLE_BYTES) },
        ),
      );
      return;
    }
    uploadMutation.mutate(file);
  };

  const nameInvalid = name.length > 0 && !NAME_PATTERN.test(name);
  const descriptionOverLimit = description.length > MAX_DESCRIPTION_LENGTH;
  const instructionsOverLimit = instructions.length > MAX_INSTRUCTIONS_LENGTH;
  const canSubmit =
    NAME_PATTERN.test(name) &&
    description.trim().length > 0 &&
    !descriptionOverLimit &&
    instructions.trim().length > 0 &&
    !instructionsOverLimit;

  const headerTitle = skill
    ? intl.formatMessage({ id: 'skills.dialog.editTitle', defaultMessage: 'Edit skill' })
    : intl.formatMessage({ id: 'skills.dialog.createTitle', defaultMessage: 'New skill' });

  const request: AgentSkillRequest = { name, description, instructions, tools, shared };

  const visibilityWarning = (): string | null => {
    if (!skill || shared === skill.shared) {
      return null;
    }
    return shared
      ? intl.formatMessage({
          id: 'skills.form.visibilityWillShare',
          defaultMessage:
            'Saving will offer this skill and its instructions to every member, switched off until each of them turns it on.',
        })
      : intl.formatMessage({
          id: 'skills.form.visibilityWillUnshare',
          defaultMessage:
            "Saving will remove this skill from every other member's agent, and any process step that uses it will need updating. Only you will be able to load it.",
        });
  };
  const visibilityChangeWarning = visibilityWarning();

  return (
    <Dialog
      show
      dataTestId="skill-form-dialog"
      headerTitle={headerTitle}
      onClose={onClose}
      onCloseIconButtonClick={onClose}
      primaryButtonLabel={intl.formatMessage({ id: 'skills.dialog.save', defaultMessage: 'Save' })}
      onPrimaryButtonClick={() => onSubmit(request)}
      primaryButtonDisabled={!canSubmit || isSaving}
      primaryButtonLoading={isSaving}
      secondaryButtonLabel={intl.formatMessage({ id: 'skills.dialog.cancel', defaultMessage: 'Cancel' })}
      onSecondaryButtonClick={onClose}
      closeOnEscape
    >
      <FormBody>
        <FieldGroup>
          <FieldLabel htmlFor="skill-name">
            {intl.formatMessage({ id: 'skills.form.name', defaultMessage: 'Name' })}
          </FieldLabel>
          <FieldInput
            id="skill-name"
            value={name}
            placeholder={intl.formatMessage({
              id: 'skills.form.namePlaceholder',
              defaultMessage: 'e.g. gdt_annotations',
            })}
            onChange={(e) => setName(e.target.value)}
          />
          {nameInvalid && (
            <FieldHint $warning>
              {intl.formatMessage({
                id: 'skills.form.nameHint',
                defaultMessage:
                  '2-64 characters: lowercase letter first, then lowercase letters, digits, _ or -',
              })}
            </FieldHint>
          )}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="skill-description">
            {intl.formatMessage({ id: 'skills.form.description', defaultMessage: 'Description' })}
          </FieldLabel>
          <FieldInput
            id="skill-description"
            value={description}
            placeholder={intl.formatMessage({
              id: 'skills.form.descriptionPlaceholder',
              defaultMessage: 'One-line summary the agent uses to decide when to load this skill',
            })}
            onChange={(e) => setDescription(e.target.value)}
          />
          <FieldHint $warning={descriptionOverLimit}>
            {description.length} / {MAX_DESCRIPTION_LENGTH}
          </FieldHint>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="skill-instructions">
            {intl.formatMessage({ id: 'skills.form.instructions', defaultMessage: 'Instructions' })}
          </FieldLabel>
          <FieldTextArea
            id="skill-instructions"
            value={instructions}
            placeholder={intl.formatMessage({
              id: 'skills.form.instructionsPlaceholder',
              defaultMessage: 'Guidance the agent receives when it loads this skill...',
            })}
            onChange={(e) => setInstructions(e.target.value)}
          />
          <FieldHint $warning={instructionsOverLimit}>
            {instructions.length} / {MAX_INSTRUCTIONS_LENGTH}
          </FieldHint>
        </FieldGroup>

        {availableTools.length > 0 && (
          <FieldGroup>
            <FieldLabel as="span" id={TOOLS_LABEL_ID}>
              {intl.formatMessage({ id: 'skills.form.tools', defaultMessage: 'Tools' })}
            </FieldLabel>
            <ToolGrid
              role="group"
              aria-labelledby={TOOLS_LABEL_ID}
              data-testid="skill-tool-picker"
            >
              {availableTools.map(({ name: tool, runner }) => {
                const badge = runnerBadgeLabel(runner);
                return (
                  <ToolOption key={tool}>
                    <Checkbox
                      id={`skill-tool-${tool}`}
                      dataTestId={`skill-tool-${tool}`}
                      checked={tools.includes(tool)}
                      onChange={() => toggleTool(tool)}
                    />
                    <ToolOptionLabel htmlFor={`skill-tool-${tool}`}>{tool}</ToolOptionLabel>
                    {badge && (
                      <ToolBadge data-testid={`skill-tool-badge-${tool}`}>{badge}</ToolBadge>
                    )}
                  </ToolOption>
                );
              })}
            </ToolGrid>
            <FieldHint>
              {intl.formatMessage({
                id: 'skills.form.toolsHint',
                defaultMessage:
                  'Extra tools the agent gains when it loads this skill. General-purpose tools such as run_code are always available.',
              })}
            </FieldHint>
          </FieldGroup>
        )}

        <FieldGroup>
          {/* Heads the group rather than labelling a control: the checkbox below
              carries its own label. */}
          <FieldLabel as="span">
            {intl.formatMessage({ id: 'skills.form.visibility', defaultMessage: 'Visibility' })}
          </FieldLabel>
          <CheckboxRow>
            <Checkbox
              id="skill-shared"
              dataTestId="skill-shared"
              checked={shared}
              onChange={setShared}
            />
            <CheckboxLabel htmlFor="skill-shared">
              {intl.formatMessage({
                id: 'skills.form.share',
                defaultMessage: 'Share with the organization',
              })}
            </CheckboxLabel>
          </CheckboxRow>
          <FieldHint>
            {intl.formatMessage({
              id: 'skills.form.shareHint',
              defaultMessage:
                "Shared skills appear on every member's Skills page switched off, so nobody's agent changes until they turn one on. Leave unchecked to keep the skill to yourself.",
            })}
          </FieldHint>
          {visibilityChangeWarning && (
            <FieldHint $warning data-testid="skill-visibility-warning">
              {visibilityChangeWarning}
            </FieldHint>
          )}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="skill-file-input">
            {intl.formatMessage({ id: 'skills.files.label', defaultMessage: 'Bundled files' })}
          </FieldLabel>
          {skillId ? (
            <>
              {files.length > 0 && (
                <FileList>
                  {files.map((file) => (
                    <FileRow key={file.filename}>
                      <FileMeta>
                        {file.filename} ({formatFileSize(file.sizeBytes)})
                      </FileMeta>
                      <SkillActions>
                        <Button
                          variant={BUTTON_VARIANT_TEXT}
                          stretch={false}
                          dataTestId={`skill-file-download-${file.filename}`}
                          disabled={downloading && downloadingFile !== file.filename}
                          loading={downloadingFile === file.filename}
                          title={intl.formatMessage(
                            {
                              id: 'skills.files.downloadTitle',
                              defaultMessage: 'Download {filename}',
                            },
                            { filename: file.filename },
                          )}
                          onClick={() => void downloadFile(file.filename)}
                        >
                          {intl.formatMessage({
                            id: 'skills.files.download',
                            defaultMessage: 'Download',
                          })}
                        </Button>
                        <Button
                          variant={BUTTON_VARIANT_TEXT}
                          stretch={false}
                          disabled={removeFileMutation.isPending}
                          onClick={() => removeFileMutation.mutate(file.filename)}
                        >
                          {intl.formatMessage({
                            id: 'skills.files.remove',
                            defaultMessage: 'Remove',
                          })}
                        </Button>
                      </SkillActions>
                    </FileRow>
                  ))}
                </FileList>
              )}
              {/* Mirrors the button rather than the cap alone: the field label
                  targets this input, so any state that disables the button but
                  not the input leaves the label as a way around it. */}
              <HiddenFileInput
                id="skill-file-input"
                type="file"
                ref={fileInputRef}
                disabled={addFileDisabled}
                onChange={onFilePicked}
              />
              <div>
                <Button
                  variant={BUTTON_VARIANT_OUTLINE}
                  stretch={false}
                  disabled={addFileDisabled}
                  title={atFileLimit ? fileLimitMessage : undefined}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {intl.formatMessage({
                    id: 'skills.files.add',
                    defaultMessage: 'Add file',
                  })}
                </Button>
              </div>
              <FieldHint>
                {intl.formatMessage(
                  {
                    id: 'skills.files.hint',
                    defaultMessage:
                      'Staged into the agent workspace under skills/{name}/ when this skill is loaded. Up to {max} per file.',
                  },
                  { name: skill?.name ?? name, max: formatFileSize(MAX_FILE_SIZE_BYTES) },
                )}
              </FieldHint>
              <FieldHint $warning={atFileLimit} data-testid="skill-files-count">
                {atFileLimit
                  ? fileLimitMessage
                  : intl.formatMessage(
                      { id: 'skills.files.count', defaultMessage: '{count} of {max} files' },
                      { count: files.length, max: MAX_FILES_PER_SKILL },
                    )}
              </FieldHint>
              <FieldHint $warning={usedBytes >= MAX_BUNDLE_BYTES} data-testid="skill-files-usage">
                {intl.formatMessage(
                  { id: 'skills.files.usage', defaultMessage: '{used} of {max} used' },
                  { used: formatFileSize(usedBytes), max: formatFileSize(MAX_BUNDLE_BYTES) },
                )}
              </FieldHint>
              {fileError && <FieldHint $warning>{fileError}</FieldHint>}
            </>
          ) : (
            <FieldHint>
              {intl.formatMessage({
                id: 'skills.files.saveFirst',
                defaultMessage: 'Save the skill first, then edit it to attach files.',
              })}
            </FieldHint>
          )}
        </FieldGroup>

        {serverError && <FormError>{serverError}</FormError>}
      </FormBody>
    </Dialog>
  );
};

export default SkillFormDialog;
